import { useState } from "react";
import dayjs from "dayjs";
import { Chip } from "@mui/material";

import {
  FaBed,
  FaBroom,
  FaHandPaper,
  FaSignInAlt,
  FaSignOutAlt,
  FaStickyNote,
  FaUser,
  FaUserPlus,
} from "react-icons/fa";

import VuiBox from "components/VuiBox";
import VuiTypography from "components/VuiTypography";
import VuiButton from "components/VuiButton";
import VuiTooltip from "components/VuiTooltip";
import GuestFlagChip from "components/GuestFlagChip";

import usePermissions from "hooks/usePermissions";

import colors from "assets/theme/base/colors";
import rgba from "assets/theme/functions/rgba";

import { getResourceStateConfig, getOccupancyStateConfig } from "../constants";

import ClaimRoomDialog from "./ClaimRoomDialog";

const { dark, primary, light } = colors;

const formatTime = (time) => {
  if (!time) return "—";

  const value = String(time);

  if (/^\d{2}:\d{2}/.test(value)) {
    const parsed = dayjs(`2000-01-01T${value}`);

    return parsed.isValid() ? parsed.format("hh:mm A") : value;
  }

  const parsed = dayjs(value);

  return parsed.isValid() ? parsed.format("hh:mm A") : value;
};

const getGuestName = (room) =>
  room?.guestName ||
  room?.guest?.name ||
  room?.guest?.fullName ||
  room?.reservation?.guestName ||
  room?.reservation?.guest?.name ||
  "";

const getAssignedStaffName = (room) =>
  room?.assignedUserName || room?.assignedStaff?.name || room?.assignedStaff?.email || "Unassigned";

const getGuestFlag = (room) =>
  room?.guestFlag || room?.guest?.flag || room?.reservation?.guestFlag || null;

const getRoomNote = (room) => {
  if (!room?.note?.text) {
    return null;
  }

  return room.note;
};

function HousekeepingRoomCard({ room, onView, onAssign, onUpdateStatus }) {
  const { isHouseKeeper, can } = usePermissions();

  const [claimDialogOpen, setClaimDialogOpen] = useState(false);

  const resourceState = getResourceStateConfig(room?.resourceState);

  const occupancyState = getOccupancyStateConfig(room?.occupancyState);

  const guestName = getGuestName(room);

  const guestFlag = getGuestFlag(room);

  const assignedStaff = getAssignedStaffName(room);

  const roomNote = getRoomNote(room);

  const isAssigned = Boolean(
    room?.assignedUserId ||
    room?.assignedStaff?._id ||
    room?.assignedStaff?.id ||
    room?.assignedToUserId
  );

  return (
    <>
      <VuiBox
        p={1.5}
        borderRadius="18px"
        display="flex"
        flexDirection="column"
        sx={{
          position: "relative",
          overflow: "hidden",
          border: `1px solid ${rgba(dark.main, 0.45)}`,
          transition: "all 0.25s ease",

          "&:hover": {
            transform: "translateY(-2px)",
            borderColor: rgba(primary.main, 0.3),
          },
        }}
      >
        {/* Header */}
        <VuiBox
          display="flex"
          justifyContent="space-between"
          alignItems="flex-start"
          gap={1.2}
          mb={1}
        >
          <VuiBox display="flex" alignItems="center" gap={1.1} minWidth={0}>
            <VuiBox
              width={38}
              height={38}
              borderRadius="11px"
              display="flex"
              alignItems="center"
              justifyContent="center"
              flexShrink={0}
              sx={{
                background: rgba(primary.main, 0.08),
                border: `1px solid ${rgba(primary.main, 0.18)}`,
              }}
            >
              <FaBed size={15} color={primary.main} />
            </VuiBox>

            <VuiBox minWidth={0}>
              <VuiTypography
                fontWeight="medium"
                sx={{
                  display: "block",
                  lineHeight: 1.2,
                  overflow: "hidden",
                  whiteSpace: "nowrap",
                  textOverflow: "ellipsis",
                }}
              >
                {room?.roomName || room?.roomNumber || "Room"}
              </VuiTypography>
            </VuiBox>
          </VuiBox>

          <Chip
            size="small"
            label={resourceState.label}
            sx={{
              flexShrink: 0,
              height: 25,
              background: rgba(resourceState.color, 0.1),
              color: resourceState.color,
              border: `1px solid ${rgba(resourceState.color, 0.3)}`,
              fontWeight: 500,
              fontSize: "12px",

              "& .MuiChip-label": {
                px: 1,
              },
            }}
          />
        </VuiBox>

        {/* Guest */}
        <VuiBox
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          gap={1}
          mb={1}
          minHeight={28}
        >
          <VuiBox display="flex" alignItems="center" gap={0.8} minWidth={0}>
            <FaUser
              size={11}
              color={guestName ? primary.main : rgba(light.main, 0.4)}
              style={{
                flexShrink: 0,
              }}
            />

            <VuiTypography
              variant="caption"
              sx={{
                minWidth: 0,
                overflow: "hidden",
                whiteSpace: "nowrap",
                textOverflow: "ellipsis",
                opacity: guestName ? 1 : 0.5,
                fontStyle: guestName ? "normal" : "italic",
              }}
            >
              {guestName || "No active guest"}
            </VuiTypography>
          </VuiBox>

          {guestFlag && <GuestFlagChip flag={guestFlag} maxWidth={120} />}
        </VuiBox>

        {/* Occupancy / Assignment */}
        <VuiBox display="grid" gridTemplateColumns="1fr 1fr" gap={1} mb={1}>
          <VuiBox
            p={1}
            borderRadius="10px"
            sx={{
              background: "rgba(255,255,255,0.025)",
              border: "1px solid rgba(255,255,255,0.06)",
            }}
          >
            <VuiTypography
              variant="caption"
              sx={{
                display: "block",
                mb: 0.35,
                opacity: 0.45,
              }}
            >
              Occupancy
            </VuiTypography>

            <VuiBox display="flex" alignItems="center" gap={0.6}>
              <VuiBox
                width={6}
                height={6}
                borderRadius="50%"
                sx={{
                  background: occupancyState.color,
                  flexShrink: 0,
                }}
              />

              <VuiTypography
                variant="caption"
                sx={{
                  color: occupancyState.color,
                  fontWeight: 500,
                }}
              >
                {occupancyState.label}
              </VuiTypography>
            </VuiBox>
          </VuiBox>

          <VuiBox
            p={1}
            borderRadius="10px"
            sx={{
              background: "rgba(255,255,255,0.025)",
              border: "1px solid rgba(255,255,255,0.06)",
            }}
          >
            <VuiTypography
              variant="caption"
              sx={{
                display: "block",
                mb: 0.35,
                opacity: 0.45,
              }}
            >
              Assigned To
            </VuiTypography>

            <VuiTypography
              variant="caption"
              sx={{
                display: "block",
                overflow: "hidden",
                whiteSpace: "nowrap",
                textOverflow: "ellipsis",
              }}
            >
              {assignedStaff}
            </VuiTypography>
          </VuiBox>
        </VuiBox>

        {/* Arrival / Departure */}
        {(room?.isArrival || room?.isDeparture) && (
          <VuiBox
            display="grid"
            gridTemplateColumns={room?.isArrival && room?.isDeparture ? "1fr 1fr" : "1fr"}
            gap={1}
            mb={1}
          >
            {room?.isArrival && (
              <VuiBox
                display="flex"
                alignItems="center"
                gap={0.7}
                p={0.9}
                borderRadius="9px"
                sx={{
                  background: "rgba(59,130,246,0.06)",
                  border: "1px solid rgba(59,130,246,0.12)",
                }}
              >
                <FaSignInAlt size={10} color="#3b82f6" />

                <VuiBox>
                  <VuiTypography
                    variant="caption"
                    sx={{
                      display: "block",
                      opacity: 0.5,
                    }}
                  >
                    Arrival
                  </VuiTypography>

                  <VuiTypography
                    variant="caption"
                    sx={{
                      display: "block",
                    }}
                  >
                    {formatTime(room.arrivalTime)}
                  </VuiTypography>
                </VuiBox>
              </VuiBox>
            )}

            {room?.isDeparture && (
              <VuiBox
                display="flex"
                alignItems="center"
                gap={0.7}
                p={0.9}
                borderRadius="9px"
                sx={{
                  background: "rgba(245,158,11,0.06)",
                  border: "1px solid rgba(245,158,11,0.12)",
                }}
              >
                <FaSignOutAlt size={10} color="#f59e0b" />

                <VuiBox>
                  <VuiTypography
                    variant="caption"
                    sx={{
                      display: "block",
                      opacity: 0.5,
                    }}
                  >
                    Departure
                  </VuiTypography>

                  <VuiTypography
                    variant="caption"
                    sx={{
                      display: "block",
                    }}
                  >
                    {formatTime(room.departureTime)}
                  </VuiTypography>
                </VuiBox>
              </VuiBox>
            )}
          </VuiBox>
        )}

        {/* Note */}
        <VuiBox
          p={1.1}
          mb={1.5}
          borderRadius="10px"
          sx={{
            background: rgba(primary.main, 0.035),
            border: `1px solid ${rgba(primary.main, 0.12)}`,
          }}
        >
          <VuiBox
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            gap={1}
            mb={roomNote ? 0.6 : 0}
          >
            <VuiBox display="flex" alignItems="center" gap={0.6}>
              <FaStickyNote size={10} color={primary.main} />

              <VuiTypography
                variant="caption"
                sx={{
                  fontWeight: 500,
                }}
              >
                Note
              </VuiTypography>
            </VuiBox>
          </VuiBox>

          {roomNote ? (
            <>
              <VuiTooltip title={roomNote.text || ""}>
                <VuiTypography
                  variant="caption"
                  sx={{
                    display: "-webkit-box",
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: "vertical",
                    overflow: "hidden",
                    lineHeight: 1.45,
                  }}
                >
                  {roomNote.text}
                </VuiTypography>
              </VuiTooltip>

              {roomNote.updatedAt && dayjs(roomNote.updatedAt).isValid() && (
                <VuiTypography
                  variant="caption"
                  sx={{
                    display: "block",
                    mt: 0.55,
                    fontSize: "11px",
                    opacity: 0.75,
                  }}
                >
                  Updated {dayjs(roomNote.updatedAt).format("MMM DD, hh:mm A")}
                </VuiTypography>
              )}
            </>
          ) : (
            <VuiTypography
              variant="caption"
              sx={{
                display: "block",
                fontSize: "10px",
                opacity: 0.45,
                mt: 0.55,
              }}
            >
              No housekeeping note
            </VuiTypography>
          )}
        </VuiBox>

        {/* Actions */}
        <VuiBox mt="auto" display="flex" gap={1}>
          {isHouseKeeper ? (
            <VuiTooltip title={isAssigned ? "Room already assigned" : "Claim this room"}>
              <span>
                <VuiButton
                  variant="outlined"
                  color="primary"
                  size="small"
                  sx={{
                    minWidth: "38px",
                    width: "38px",
                    height: "36px",
                    p: 0,
                    borderRadius: "10px",
                    border: `1px solid ${rgba(dark.main, 0.5)}`,
                  }}
                  disabled={isAssigned}
                  onClick={() => setClaimDialogOpen(true)}
                >
                  <FaHandPaper size={12} />
                </VuiButton>
              </span>
            </VuiTooltip>
          ) : (
            can("editHouseKeeping") && (
              <VuiTooltip title={isAssigned ? "Reassign Room" : "Assign Room"}>
                <VuiButton
                  variant="outlined"
                  color="primary"
                  size="small"
                  sx={{
                    minWidth: "38px",
                    width: "38px",
                    height: "36px",
                    p: 0,

                    "&:hover": {
                      background: rgba(primary.main, 0.1),
                      borderColor: rgba(primary.main, 0.4),
                    },
                  }}
                  onClick={() => onAssign?.(room)}
                >
                  <FaUserPlus size={12} />
                </VuiButton>
              </VuiTooltip>
            )
          )}

          <VuiButton
            variant="outlined"
            size="small"
            color="primary"
            fullWidth
            onClick={() => onView?.(room)}
          >
            Details
          </VuiButton>

          {can("editHouseKeeping") && (
            <VuiButton
              variant="outlined"
              color="primary"
              size="small"
              fullWidth
              onClick={() => onUpdateStatus?.(room)}
            >
              Update
            </VuiButton>
          )}
        </VuiBox>
      </VuiBox>

      <ClaimRoomDialog
        open={claimDialogOpen}
        room={room}
        onClose={() => setClaimDialogOpen(false)}
      />
    </>
  );
}

export default HousekeepingRoomCard;
